import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { FilterParams, SortKey } from "../types";
import { parseSortDirection } from "@/components/tables/types";

interface UseAllQueriesFiltersProps {
  queryHash?: string;
}

export function useAllQueriesFilters({ queryHash }: UseAllQueriesFiltersProps) {
  const { getFilterValue, isInitialized } = useDatatableContext();
  const { page, pageSize } = useTablePagination();
  const [searchParams] = useSearchParams();

  // Get date filters from context
  const startDate = useMemo(
    () => getFilterValue<Date>("start_date") || dayjs().subtract(28, "day").toDate(),
    [getFilterValue]
  );
  const endDate = useMemo(
    () => getFilterValue<Date>("end_date") || dayjs().toDate(),
    [getFilterValue]
  );

  // Query-specific filter getters
  const tagFilter = useMemo(() => getFilterValue<string[]>("tags") || [], [getFilterValue]);
  const warehouseFilter = useMemo(
    () => getFilterValue<string[]>("warehouses") || [],
    [getFilterValue]
  );
  const userFilter = useMemo(() => getFilterValue<string[]>("users") || [], [getFilterValue]);
  const queryTypeFilter = useMemo(
    () => getFilterValue<string[]>("queryType") || [],
    [getFilterValue]
  );
  const instanceFilter = useMemo(
    () => getFilterValue<string[]>("instance") || [],
    [getFilterValue]
  );
  // Read teams directly from URL params since it's not a registered column filter.
  // This param is set by the Team Spends cost attribution link to pre-filter by team.
  const teamsFilter = useMemo(() => searchParams.getAll("teams") || [], [searchParams]);

  // Text filters
  const executionTimeFilter = useMemo(
    () => getFilterValue<string>("executionTime"),
    [getFilterValue]
  );
  const executionTimeFilterMode = useMemo(
    () => (getFilterValue<string>("executionTimeFilterMode") || "gt") as "lt" | "bt" | "gt",
    [getFilterValue]
  );
  const queryCostFilter = useMemo(() => getFilterValue<string>("queryCost"), [getFilterValue]);
  const executionCostFilterMode = useMemo(
    () => (getFilterValue<string>("executionCostFilterMode") || "gt") as "lt" | "bt" | "gt",
    [getFilterValue]
  );

  // Search filters - read directly from URL params since they're not registered in context
  const queryId = useMemo(() => searchParams.get("queryId") || "", [searchParams]);
  const sessionIDFilter = useMemo(() => searchParams.get("sessionID") || "", [searchParams]);
  const queryParameterizedHash = useMemo(
    () => searchParams.get("queryParameterizedHash") || "",
    [searchParams]
  );

  // Sort filters - read from URL params
  const sortAttribute = useMemo(
    () => (searchParams.get("sortAttribute") || "query_cost") as SortKey,
    [searchParams]
  );
  const sortOrder = useMemo(
    () => parseSortDirection(searchParams.get("sortOrder"), "desc"),
    [searchParams]
  );

  // Convert filters to API parameters
  const filterParams = useMemo((): FilterParams => {
    const params: FilterParams = {
      page: page + 1,
      size: pageSize,
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate)!.toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)!.toISOString(),
    };

    if (queryHash) params.query_hash = queryHash;
    if (queryParameterizedHash) params.query_parameterized_hash = queryParameterizedHash;
    if (queryId) params.query_id = queryId;
    if (warehouseFilter.length > 0) params.warehouses = warehouseFilter;
    if (tagFilter.length > 0) params.tags = tagFilter;
    if (userFilter.length > 0) params.users = userFilter;
    if (instanceFilter.length > 0) params.instance_ids = instanceFilter;
    if (queryTypeFilter.length > 0) params.query_type = queryTypeFilter;
    if (teamsFilter.length > 0) params.teams = teamsFilter;

    if (sortAttribute && sortOrder) {
      params.sortAttribute = sortAttribute;
      params.sortOrder = sortOrder;
    }

    if (executionTimeFilter) {
      params.executionTimeFilter = executionTimeFilter;
      params.executionTimeFilterMode = executionTimeFilterMode;
    }

    if (queryCostFilter) {
      params.executionCostFilter = queryCostFilter;
      params.executionCostFilterMode = executionCostFilterMode;
    }

    if (sessionIDFilter) params.session_id = sessionIDFilter;

    return params;
  }, [
    page,
    pageSize,
    startDate,
    endDate,
    queryHash,
    queryParameterizedHash,
    queryId,
    warehouseFilter,
    tagFilter,
    userFilter,
    instanceFilter,
    queryTypeFilter,
    teamsFilter,
    sortAttribute,
    sortOrder,
    executionTimeFilter,
    executionTimeFilterMode,
    queryCostFilter,
    executionCostFilterMode,
    sessionIDFilter,
  ]);

  return {
    filterParams,
    isInitialized,
    // Export individual filter values for use in components
    tagFilter,
    warehouseFilter,
    userFilter,
    queryTypeFilter,
    instanceFilter,
    executionTimeFilter,
    executionTimeFilterMode,
    queryCostFilter,
    executionCostFilterMode,
    queryId,
    sessionIDFilter,
    queryParameterizedHash,
    startDate,
    endDate,
    sortAttribute,
    sortOrder,
  };
}

export type { SortKey };
